import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";

/**
 * Protects a form with unsaved changes:
 * - closing or reloading the tab triggers the browser's own prompt (the only
 *   kind a browser allows there);
 * - clicking any in-app link, including the sidebar, asks first with an
 *   in-app dialog;
 * - buttons that navigate in code wrap the navigation in `guard()`.
 * The app uses <BrowserRouter>, which has no navigation-blocking API, hence
 * the capture-phase click listener on links.
 */
export function useUnsavedChanges(dirty: boolean) {
  const navigate = useNavigate();
  const [pending, setPending] = useState<(() => void) | null>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link || link.target === "_blank" || link.origin !== window.location.origin) return;
      e.preventDefault();
      e.stopPropagation();
      const to = link.pathname + link.search + link.hash;
      setPending(() => () => navigate(to));
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [navigate]);

  const guard = useCallback((go: () => void) => {
    if (dirtyRef.current) setPending(() => go); else go();
  }, []);

  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      title="Leave without saving?"
      body="You have unsaved changes on this page. If you leave now they will be lost."
      cancelLabel="Stay on this page"
      confirmLabel="Leave without saving"
      tone="danger"
      onCancel={() => setPending(null)}
      onConfirm={() => { const go = pending; setPending(null); dirtyRef.current = false; go?.(); }}
    />
  );

  return { guard, dialog };
}
